import { useTranslation } from 'react-i18next'
import Sheet from '../../components/Sheet'
import ActionBar from '../../components/ActionBar'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import InlineLoader from '../../loading/InlineLoader'

// One product, found and shown before anything is offered to do to it.
//
// Scanning a tag on the shop floor is most often a "what is this, and how many
// do we have" question. The old answer to it was the pencil: an icon that
// opened a bare barcode prompt when nothing was selected and edited the
// selection when something was, with nothing on screen to say which. Answering
// the question first means the common case needs no taps at all, and Edit stops
// being a guess.

function Spec(props) {
    const { label, value } = props
    if (value === null || value === undefined || value === '') return null

    return (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.5 }}>
            <Typography variant='body2' color='text.secondary'>{label}</Typography>
            <Typography variant='body2' sx={{ fontWeight: 500, textAlign: 'right' }}>{value}</Typography>
        </Box>
    )
}

export default function ProductSheet(props) {
    const { open, onClose, product, onEdit, onPrint, onImageClick, loading = false, primaryAction = 'edit' } = props
    const { t } = useTranslation()

    if (!product && !loading) return null

    // A lookup is a Shopify round trip. useFetch's rule for a read is a
    // loadingKey plus something that renders it, and the region about to change
    // is this sheet -- so it opens on the tap and fills in, rather than leaving
    // the list sitting there looking like nothing happened.
    if (loading) {
        return (
            <Sheet open={open} onClose={onClose} title={t('common.loading')}>
                <InlineLoader />
            </Sheet>
        )
    }

    const metal = product.metafields?.metal?.value

    // Which button leads follows why the sheet opened. On Products a scanned,
    // tagged item is most often about to be edited; on Find a Piece the item
    // was looked up precisely because it has no readable tag, so printing one
    // leads there instead.
    const printAction = onPrint ? { label: t('products.printTag'), onClick: () => onPrint(product) } : undefined
    const editAction = onEdit ? { label: t('common.edit'), onClick: () => onEdit(product) } : undefined
    const printLeads = primaryAction === 'print'

    return (
        <Sheet
            open={open}
            onClose={onClose}
            title={product.title}
            actions={
                (onPrint || onEdit) && (
                    <ActionBar
                        secondary={printLeads ? editAction : printAction}
                        confirm={printLeads ? printAction : editAction}
                    />
                )
            }
        >
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                {product.image && (
                    <Box
                        component='img'
                        src={product.image}
                        alt='product'
                        onClick={onImageClick ? () => onImageClick(product.image) : undefined}
                        sx={{
                            cursor: onImageClick ? 'pointer' : 'default',
                            width: 96,
                            height: 96,
                            flex: 'none',
                            objectFit: 'cover',
                            borderRadius: '8px',
                            bgcolor: 'action.hover'
                        }}
                    />
                )}
                <Box sx={{ minWidth: 0 }}>
                    <Typography
                        variant='body2'
                        color='text.secondary'
                        sx={{ fontVariantNumeric: 'tabular-nums' }}
                    >
                        {product.barcode}
                    </Typography>
                    <Typography variant='h6'>{`$${product.price}`}</Typography>
                </Box>
            </Box>

            <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 1 }}>
                <Spec label={t('products.columns.category')} value={product.category} />
                <Spec label={t('products.columns.metal')} value={metal} />
                {/* Both shops, always -- "we have one at the other store" is the
                    answer that saves a phone call. */}
                <Spec
                    label={t('products.newark')}
                    value={t('products.inStock', { count: product.nwkQuantity || 0 })}
                />
                <Spec
                    label={t('products.longBranch')}
                    value={t('products.inStock', { count: product.lbQuantity || 0 })}
                />
            </Box>
        </Sheet>
    )
}
